import React, {FC, useState} from 'react';
import styled from 'styled-components';
import { AppType, CollectionType } from '../../constants';

import App from '../../models/app';
import { ICollection } from '../../types/collection';
import HomeContainer from './HomeContainer';
import {ToggleItem, ToggleLabels} from './Labels/index';
import PremiumCollectionHeader from './Premium/PremiumCollectionHeader';
import { Transition } from './Transition';
import {FilterType} from './Labels/types';
import i18n from '../../i18n';

const Page = styled.div`
  background: var(--sys-color--surface);
  box-sizing: border-box;
  min-height: 100%;
  width: 100%;
`;

interface CollectionProps {
  apps: App[];
  appDigestProps: {
    onRemove: () => void;
    showSettings?: boolean;
    showInstalled?: boolean;
  };
  collection: ICollection;
  isApp: boolean;
  appType: AppType;
  userHasGalleryGrant: boolean;
  ListPage: any;
  userFlags: FilterType[],

  // todo check these types
  fetchMore: any;
  hasNextPage: any;
  loading: any;
  error: any;
}

// Get the toggle labels and the initial states.
const initialToggleLabels = (userFlags: FilterType[])
    : ToggleItem<FilterType>[] =>
    Object.values(FilterType).map((value) => {
    const item: ToggleItem<FilterType> = {
      value: value,
      text: i18n(`page.Collection.Filters.${value.toUpperCase()}`)(),
      isToggled: userFlags.indexOf(value) >= 0,
    }
    return item;
  })

// Get the values of the toggled fields(where isToggled is true).
const toggledLabelValues = (items: ToggleItem<FilterType>[]) =>
  items.filter((item) => item.isToggled)
      .map((item) => item.value);

interface RenderToggleLabelsProps {
  toggleLabels: ToggleItem<FilterType>[];
  collection: ICollection;
  fetchMore: any;
}

// Renders the toggle label fields.
const renderToggleLabels = (
    { toggleLabels,
      collection,
      fetchMore,
    }: RenderToggleLabelsProps) => {
  if(toggleLabels && toggleLabels.length > 0) {
    // Keep a copy of the selected filters for use when the onValuesChanged
    // event is triggered.
    const previousToggleLabelsValues: FilterType[] = toggleLabels
        .filter((item) => item.isToggled)
        .map((item) => item.value);

    // Previously selected filter values.
    const previousCollectionFilters: string[] = previousToggleLabelsValues
        .map((f) => f.toUpperCase());

    return (<ToggleLabels
        values={toggleLabels}
        onValuesChanged={() => {
          // Currently selected filter values.
          const collectionFilters: string[] = toggledLabelValues(toggleLabels)
              .map((f) => f.toUpperCase());

          // Most recently selected filter value or undefined if a filter was
          // deselected rather than selected.
          const selectedFilter = collectionFilters.filter((val) =>
              !previousCollectionFilters.includes(val)).pop();

          // Most recently deselected filter value or undefined if a filter was
          // selected rather than deselected.
          const deselectedFilter = previousCollectionFilters.filter((val) =>
              !collectionFilters.includes(val)).pop();

          // Redo a fetch to get items using the new flags.
          fetchMore(collection.id,
              collection.type,
              0,
              toggledLabelValues(toggleLabels), true)
        }}/>)
  }
  return undefined;
}

export const Collection: FC<CollectionProps> = (props) => {
  const [toggleLabels] = useState(initialToggleLabels(props.userFlags));
  const {
    loading,
    error,
    appDigestProps,
    apps,
    isApp,
    hasNextPage,
    collection,
    userHasGalleryGrant,
    ListPage,
    fetchMore
  } = props;

  let content;
  if ((!apps || apps.length === 0) && loading || error) {
    content = (<Transition loading={loading} error={error} headerHeight={44} />);
  } else {
    let listHeader;
    if (collection.isPremium && !isApp) {
      listHeader = (<PremiumCollectionHeader
        userHasGalleryGrant={userHasGalleryGrant}
        subscribeButtonTrigger={`collection-${collection.id}`}
      />)
    }
    content = (
      <Page className="app-container">
        {renderToggleLabels({
          toggleLabels,
          collection,
          fetchMore,
        })}
        <ListPage
          apps={apps}
          hasNextPage={hasNextPage}
          appDigestProps={appDigestProps}
          fetchMore={({ startIndex }: any) =>
            fetchMore(collection.id, collection.type, startIndex, toggledLabelValues(toggleLabels))
          }
          navbarHeight={132}
          listHeader={listHeader}
          collectionId={collection.type.toLowerCase() === CollectionType.COLLECTION.toLowerCase() ? collection.id : undefined}
          appType={props.appType}
        />
      </Page>
    );
  }

  return (
    <div>
      <HomeContainer
        centerTitle={collection.name}
        showTitle={true}
      >
        {content}
      </HomeContainer>
    </div>
  );
};